import React, { useEffect, useRef, useState } from 'react';
import { X, Play, Pause, Repeat, Volume2, Timer, MessageSquare, Music2, StickyNote, BookOpen, Star, Moon, Share2, Copy, Check as CheckIcon } from 'lucide-react';
import { ambientSounds } from '../data/mock';
import useAmbientAudio from '../hooks/useAmbientAudio';
import useYouTubePlayer from '../hooks/useYouTubePlayer';

// Extract a YouTube video ID from a full URL or return the raw ID as-is.
const extractYouTubeId = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  // Already looks like an ID (11 chars, alphanum + - _)
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
  try {
    // youtu.be short link
    const shortMatch = s.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);
    if (shortMatch) return shortMatch[1];
    // /embed/, /shorts/, /v/
    const pathMatch = s.match(/youtube\.com\/(?:embed|shorts|v)\/([A-Za-z0-9_-]{11})/);
    if (pathMatch) return pathMatch[1];
    // watch?v=
    const url = new URL(s.startsWith('http') ? s : `https://${s}`);
    const v = url.searchParams.get('v');
    if (v && /^[A-Za-z0-9_-]{11}$/.test(v)) return v;
  } catch (_e) { /* ignore */ }
  return null;
};

const PlayerModal = ({ session, onClose }) => {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [repeat, setRepeat] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [timerMin, setTimerMin] = useState(0);
  const [timerRemaining, setTimerRemaining] = useState(0); // seconds remaining
  const [fading, setFading] = useState(false);
  const [activeTab, setActiveTab] = useState('note');
  const [mixer, setMixer] = useState({});
  const [journal, setJournal] = useState('');
  const [reflection, setReflection] = useState({ name: '', location: '', rating: 5, comment: '' });
  const [reflections, setReflections] = useState([]);
  const [volume, setVolume] = useState(0.9);
  const [shareCopied, setShareCopied] = useState(false);
  const totalSec = (session?.duration || 5) * 60;
  const progRef = useRef(null);
  const timerRef = useRef(null);

  const youTubeId = extractYouTubeId(session?.youtubeId);
  const isYouTube = !!youTubeId;

  const yt = useYouTubePlayer({
    videoId: isYouTube ? youTubeId : null,
    containerId: 'wwm-yt-mount',
    onEnded: () => { if (repeat) { yt.seek(0); yt.play(); } },
  });

  const { play, pause, fadeOutAndStop, playing: audioPlaying } = useAmbientAudio({
    url: !isYouTube ? (session?.audioUrl || null) : null,
    mixer,
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem('wwm_journal_' + session?.id);
      if (saved) setJournal(saved);
      const savedRefs = JSON.parse(localStorage.getItem('wwm_refs_' + session?.id) || '[]');
      setReflections(savedRefs);
    } catch (_e) { /* ignore */ }
  }, [session?.id]);

  // Auto-start audio when modal opens (for non-YouTube formats)
  useEffect(() => {
    if (!session || isYouTube) return;
    (async () => {
      await play();
      setPlaying(true);
    })();
    return () => { pause(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  // Progress tick
  useEffect(() => {
    if (!playing) return () => {};
    progRef.current = setInterval(() => {
      setProgress((p) => {
        if (p >= totalSec) {
          if (repeat) return 0;
          setPlaying(false);
          pause();
          return totalSec;
        }
        return p + speed;
      });
    }, 1000);
    return () => clearInterval(progRef.current);
  }, [playing, speed, repeat, totalSec, pause]);

  // Sleep timer countdown + fade-out in last 15 seconds
  useEffect(() => {
    if (!timerMin) { setTimerRemaining(0); setFading(false); return () => {}; }
    setTimerRemaining(timerMin * 60);
    setFading(false);
    let ytStartVol = 0;
    timerRef.current = setInterval(() => {
      setTimerRemaining((r) => {
        if (r <= 1) {
          clearInterval(timerRef.current);
          setPlaying(false);
          setTimerMin(0);
          setFading(false);
          if (isYouTube) { yt.pause(); yt.setVolume(ytStartVol || 80); }
          return 0;
        }
        if (r === 16) { // start 15s fade
          setFading(true);
          fadeOutAndStop(15);
          if (isYouTube) { ytStartVol = yt.volume; }
        }
        if (r < 16 && r > 1 && isYouTube) {
          const remainingFade = r - 1;
          const newVol = Math.max(0, Math.round((ytStartVol || 80) * (remainingFade / 15)));
          yt.setVolume(newVol);
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerMin]);

  const togglePlay = async () => {
    if (isYouTube) {
      if (yt.playing) { yt.pause(); }
      else { yt.play(); }
      return;
    }
    if (playing) { pause(); setPlaying(false); }
    else { await play(); setPlaying(true); }
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const changeVolume = (v) => {
    setVolume(v);
    // Volume controlled via the hook's masterRef indirectly. Use HTMLAudio if available.
    try {
      const audios = document.querySelectorAll('audio');
      audios.forEach((a) => { a.volume = v; });
    } catch (_e) { /* ignore */ }
  };

  const saveJournal = (v) => {
    setJournal(v);
    try { localStorage.setItem('wwm_journal_' + session?.id, v); } catch (_e) { /* ignore */ }
  };

  const postReflection = (e) => {
    e.preventDefault();
    if (!reflection.name.trim() || !reflection.comment.trim()) return;
    const entry = { ...reflection, id: Date.now(), date: new Date().toISOString().slice(0, 10) };
    const updated = [entry, ...reflections];
    setReflections(updated);
    try { localStorage.setItem('wwm_refs_' + session?.id, JSON.stringify(updated)); } catch (_e) { /* ignore */ }
    setReflection({ name: '', location: '', rating: 5, comment: '' });
  };

  const closeAndStop = () => {
    if (isYouTube) { try { yt.pause(); } catch (_e) { /* ignore */ } }
    pause();
    setPlaying(false);
    onClose && onClose();
  };

  const shareLink = () => {
    const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, '');
    const url = `${base}/api/share/${session.id}`;
    try {
      navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (_e) {
      window.prompt('Copy this link:', url);
    }
  };

  if (!session) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4" onClick={closeAndStop}>
      <div className="w-full max-w-4xl max-h-[95vh] overflow-y-auto rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="relative aspect-[16/9] bg-black">
          {isYouTube ? (
            <div id="wwm-yt-mount" className="absolute inset-0 w-full h-full bg-black" />
          ) : (
            <img src={session.image} alt={session.title} className={`absolute inset-0 w-full h-full object-cover transition-all duration-1000 ${playing ? 'scale-105' : 'scale-100'}`} />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          {!isYouTube && playing && (
            <div className="absolute inset-0 pointer-events-none">
              {[0, 1, 2].map((i) => (
                <span key={i} className="absolute inset-0 rounded-full border border-white/20 animate-ping" style={{ animationDelay: `${i * 0.9}s`, animationDuration: '3s' }} />
              ))}
            </div>
          )}
          <div className="absolute top-3 right-3 flex items-center gap-2">
            <button onClick={shareLink} className="inline-flex items-center gap-1.5 rounded-full bg-white/90 hover:bg-white px-3 py-1.5 text-xs font-medium text-emerald-900" title="Copy shareable link">
              {shareCopied ? <><CheckIcon className="w-3.5 h-3.5" /> Copied!</> : <><Share2 className="w-3.5 h-3.5" /> Share</>}
            </button>
            <button onClick={closeAndStop} className="p-2 rounded-full bg-white/90 hover:bg-white" aria-label="Close"><X className="w-5 h-5 text-emerald-900" /></button>
          </div>
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest">
              <span className="rounded-full bg-white/20 backdrop-blur px-2 py-0.5">{session.langFlag} {session.language}</span>
              <span className="rounded-full bg-emerald-600/90 px-2 py-0.5">{session.category}</span>
              <span className="rounded-full bg-white/20 backdrop-blur px-2 py-0.5">{session.format}</span>
              <span className="rounded-full bg-white/20 backdrop-blur px-2 py-0.5">Repeat: {repeat ? 'ON' : 'OFF'}</span>
              {timerRemaining > 0 && (
                <span className={`ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${fading ? 'bg-amber-500/90' : 'bg-emerald-500/90'}`}>
                  <Moon className="w-3 h-3" /> {fading ? 'Fading out' : 'Sleep timer'} · {fmt(timerRemaining)}
                </span>
              )}
            </div>
            <h3 className="mt-2 font-serif text-xl sm:text-2xl leading-tight max-w-3xl">{session.title}</h3>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          <div className="flex items-center gap-3">
            <button onClick={togglePlay} disabled={isYouTube && !yt.ready} className="w-12 h-12 rounded-full bg-emerald-700 text-white flex items-center justify-center hover:bg-emerald-800 shadow-md shadow-emerald-200 disabled:opacity-60">
              {(isYouTube ? yt.playing : playing) ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 fill-white ml-0.5" />}
            </button>
            <div className="flex-1">
              <div className="relative h-6 flex items-center">
                <input
                  type="range"
                  min={0}
                  max={isYouTube ? Math.max(1, Math.floor(yt.duration)) : totalSec}
                  step={1}
                  value={isYouTube ? Math.floor(yt.currentTime) : progress}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (isYouTube) yt.seek(v); else setProgress(v);
                  }}
                  className="w-full accent-emerald-700 cursor-pointer"
                  aria-label="Session progress"
                />
              </div>
              <div className="mt-1 flex justify-between text-[11px] text-emerald-800">
                <span>{fmt(isYouTube ? yt.currentTime : progress)}</span>
                <span>{fmt(isYouTube ? (yt.duration || totalSec) : totalSec)}</span>
              </div>
            </div>
            <button onClick={() => setRepeat(!repeat)} className={`p-2 rounded-full border ${repeat ? 'bg-emerald-700 text-white border-emerald-700' : 'border-emerald-200 text-emerald-800 hover:bg-emerald-50'}`} title="Repeat"><Repeat className="w-4 h-4" /></button>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-100 px-2 py-1 text-xs text-emerald-800">
              <Timer className="w-3.5 h-3.5" /> Sleep timer:
              {[0, 5, 10, 15, 30, 60].map((v) => (
                <button key={v} onClick={() => setTimerMin(v)} className={`px-2 py-0.5 rounded-full ${timerMin === v ? 'bg-emerald-700 text-white' : 'hover:bg-white'}`}>{v === 0 ? 'Off' : `${v}m`}</button>
              ))}
            </div>
            <div className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-100 px-2 py-1 text-xs text-emerald-800">
              <Volume2 className="w-3.5 h-3.5" /> Speed:
              {[0.75, 1, 1.25, 1.5, 2].map((v) => (
                <button key={v} onClick={() => { setSpeed(v); if (isYouTube) yt.setPlaybackRate(v); }} className={`px-2 py-0.5 rounded-full ${speed === v ? 'bg-emerald-700 text-white' : 'hover:bg-white'}`}>{v}x</button>
              ))}
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-100 px-3 py-1 text-xs text-emerald-800">
              <Volume2 className="w-3.5 h-3.5" />
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={isYouTube ? (yt.volume / 100) : volume}
                onChange={(e) => {
                  const v = +e.target.value;
                  if (isYouTube) yt.setVolume(v * 100); else changeVolume(v);
                }}
                className="w-24 accent-emerald-700"
              />
            </div>
            {timerRemaining > 0 && (
              <button onClick={() => { setTimerMin(0); }} className="text-xs text-emerald-800 underline">Cancel timer</button>
            )}
          </div>
          {isYouTube && (
            <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-100 p-3 flex flex-wrap items-center gap-3">
              <p className="text-[11px] text-emerald-700/80 flex-1 min-w-[220px]">
                Controlled by our custom player. If the video shows "Video unavailable", the uploader disabled embedding &mdash; open it directly on YouTube:
              </p>
              <a
                href={`https://www.youtube.com/watch?v=${youTubeId}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-xs px-4 py-2 font-medium shadow-sm"
              >
                <Play className="w-3.5 h-3.5 fill-white" /> Open in YouTube
              </a>
            </div>
          )}
          {!isYouTube && !session.audioUrl && (
            <p className="mt-2 text-[11px] text-emerald-700/80">Playing a gentle handcrafted ambient soundscape (pink noise, breath waves &amp; soft drones). Add an MP3 URL in the admin form to play your own audio.</p>
          )}
          {fading && (
            <p className="mt-2 text-[11px] text-amber-700 inline-flex items-center gap-1"><Moon className="w-3 h-3" /> Fading gently — rest well.</p>
          )}

          <div className="mt-6 border-b border-emerald-100 flex flex-wrap items-center gap-1">
            {[
              { id: 'note', label: "Creator's Note", icon: StickyNote },
              { id: 'written', label: 'Written Affirmation', icon: BookOpen },
              { id: 'mixer', label: 'Ambient Mixer', icon: Music2 },
              { id: 'reviews', label: `Community Reviews (${reflections.length})`, icon: MessageSquare },
              { id: 'journal', label: 'Post-Session Journal', icon: StickyNote },
            ].map((t) => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs border-b-2 -mb-px ${activeTab === t.id ? 'border-emerald-700 text-emerald-800 font-medium' : 'border-transparent text-emerald-700/70 hover:text-emerald-800'}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          <div className="mt-4 text-sm text-emerald-900">
            {activeTab === 'note' && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-4 italic text-emerald-900/90">
                &ldquo;{session.note}&rdquo;
                <div className="mt-2 text-right text-[11px] not-italic text-emerald-700">— Creator of winwithmeditation.com</div>
              </div>
            )}
            {activeTab === 'written' && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-4">
                {session.written ? <p className="font-serif text-lg text-emerald-900">&ldquo;{session.written}&rdquo;</p> : <p className="text-emerald-800/70">No written affirmation for this session.</p>}
              </div>
            )}
            {activeTab === 'mixer' && (
              <div>
                <p className="text-xs text-emerald-800/80 mb-3">Mix calming background soundscapes under guided voices — drag the sliders:</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {ambientSounds.map((s) => (
                    <div key={s.id} className="flex items-center gap-3 rounded-lg border border-emerald-100 p-3 bg-white">
                      <span className="text-sm font-medium text-emerald-900 w-32">{s.name}</span>
                      <input type="range" min="0" max="100" value={mixer[s.id] || 0} onChange={(e) => setMixer({ ...mixer, [s.id]: +e.target.value })} className="flex-1 accent-emerald-700" />
                      <span className="text-xs text-emerald-700 w-8 text-right">{mixer[s.id] || 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {activeTab === 'reviews' && (
              <div>
                <form onSubmit={postReflection} className="grid sm:grid-cols-2 gap-3 mb-4">
                  <input placeholder="Your Name *" value={reflection.name} onChange={(e) => setReflection({ ...reflection, name: e.target.value })} className="px-3 py-2 rounded-lg border border-emerald-200 text-sm" />
                  <input placeholder="Location (Optional)" value={reflection.location} onChange={(e) => setReflection({ ...reflection, location: e.target.value })} className="px-3 py-2 rounded-lg border border-emerald-200 text-sm" />
                  <div className="sm:col-span-2 flex items-center gap-1">
                    <span className="text-xs text-emerald-800 mr-2">Rating:</span>
                    {[1, 2, 3, 4, 5].map((n) => <button type="button" key={n} onClick={() => setReflection({ ...reflection, rating: n })}><Star className={`w-5 h-5 ${n <= reflection.rating ? 'fill-amber-500 text-amber-500' : 'text-emerald-200'}`} /></button>)}
                  </div>
                  <textarea placeholder="Your Reflection / Comment *" rows={3} value={reflection.comment} onChange={(e) => setReflection({ ...reflection, comment: e.target.value })} className="sm:col-span-2 px-3 py-2 rounded-lg border border-emerald-200 text-sm" />
                  <button type="submit" className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-emerald-700 text-white hover:bg-emerald-800">Post Reflection</button>
                </form>
                <div className="space-y-3">
                  {reflections.length === 0 && <div className="text-emerald-800/70 text-sm">Be the first to leave a reflection.</div>}
                  {reflections.map((r) => (
                    <div key={r.id} className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-emerald-950">{r.name} <span className="text-emerald-700 text-xs">{r.location && `· ${r.location}`}</span></div>
                        <div className="flex">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? 'fill-amber-500 text-amber-500' : 'text-emerald-200'}`} />)}</div>
                      </div>
                      <p className="mt-1 text-sm text-emerald-900/90">{r.comment}</p>
                      <div className="text-[10px] uppercase tracking-widest text-emerald-700 mt-1">{r.date}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {activeTab === 'journal' && (
              <div>
                <textarea rows={6} placeholder="How are you feeling after this session? Notes saved locally in your browser." value={journal} onChange={(e) => saveJournal(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-emerald-200 text-sm text-emerald-900" />
                <p className="mt-2 text-xs text-emerald-700">Your private journal notes are saved locally in your browser. No one else can see them.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerModal;
